import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";

import { useLoginMutation } from "@/api/auth/useLoginMutation";
import { RedirectIfAuthenticated } from "@/components/auth/RedirectIfAuthenticated";
import { Logo } from "@/components/brand/Logo";
import { BackgroundVideoPlaylist } from "@/components/layout/BackgroundVideoPlaylist";
import { useAuthStore } from "@/stores/auth.store";

export const Route = createFileRoute("/login")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const login = useLoginMutation();
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      const data = await login.mutateAsync(value);
      if (data) {
        setToken(data.token);
        queryClient.clear();
        await queryClient.invalidateQueries();
        void navigate({ to: "/" });
      }
    },
  });

  return (
    <RedirectIfAuthenticated>
      <div className="h-dvh relative overflow-hidden bg-black">
        {/* Background video — full bleed */}
        <BackgroundVideoPlaylist
          sources={[
            "https://test-video-ai.b-cdn.net/videos/b4d9606a-f396-43df-9c59-9d3599c32b66.mp4",
            "https://test-video-ai.b-cdn.net/videos/0ea78d60-31e5-4b3c-9fca-d74e8a82afee.mp4",
            "https://test-video-ai.b-cdn.net/videos/66fa920e-32d8-4dcc-856b-c755af2341c6.mp4",
            "https://test-video-ai.b-cdn.net/videos/59e35cf2-5aa2-471d-808c-ef14e4e78c73.mp4",
            "https://test-video-ai.b-cdn.net/videos/2ac8e671-69b6-46cf-a935-5f9f96f92bdb.mp4",
          ]}
          darkOverlay={false}
        />

        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-end md:justify-center px-6 pb-6 md:pb-0 overflow-y-auto">
          <div className="w-full max-w-sm mx-auto md:mx-0 md:ml-[10%] lg:ml-[15%]">
            {/* Logo + tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="mb-5 md:mb-8"
            >
              <div className="mb-2 md:mb-3">
                <Logo size="md" className="text-white/90 md:!h-9" />
              </div>
              <p className="text-white/40 text-xs md:text-sm leading-relaxed max-w-[280px]">
                Twórz filmy z AI. Opisz scenariusz, wybierz styl — resztą zajmie się sztuczna inteligencja.
              </p>
            </motion.div>

            {/* Form card — glass */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            >
              <div className="bg-white/[0.07] backdrop-blur-2xl border border-white/[0.09] rounded-2xl p-6 shadow-2xl">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void form.handleSubmit();
                  }}
                  className="space-y-4"
                >
                  <form.Field
                    name="email"
                    children={(field) => (
                      <div>
                        <label className="block text-[10px] font-medium uppercase tracking-[1.5px] text-white/30 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="ty@email.com"
                          value={field.state.value}
                          onChange={(e) => { field.handleChange(e.target.value); }}
                          className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent transition-all"
                        />
                      </div>
                    )}
                  />

                  <form.Field
                    name="password"
                    children={(field) => (
                      <div>
                        <label className="block text-[10px] font-medium uppercase tracking-[1.5px] text-white/30 mb-2">
                          Hasło
                        </label>
                        <input
                          type="password"
                          placeholder="••••••••"
                          value={field.state.value}
                          onChange={(e) => { field.handleChange(e.target.value); }}
                          className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent transition-all"
                        />
                      </div>
                    )}
                  />

                  <button
                    type="submit"
                    disabled={
                      login.isPending ||
                      Object.entries(form.getAllErrors().fields).length > 0
                    }
                    className="w-full bg-accent rounded-xl py-2.5 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong hover:brightness-110 disabled:opacity-50 disabled:shadow-none transition-all cursor-pointer"
                  >
                    {login.isPending ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Logowanie...
                      </span>
                    ) : (
                      "Zaloguj się"
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-white/30 hover:text-white/60 transition-colors"
                  >
                    Nie pamiętam hasła
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </RedirectIfAuthenticated>
  );
}
